import { earliestDelivery, deliveryError, editLineProperties, orderMethodHelp } from '@theme/dew-delivery-rules';
import { CartLinesUpdateEvent } from '@shopify/events';
import { syncOrderNote } from '@theme/dew-order-note';
const cartRoot = () => window.Shopify?.routes?.root || '/';
let saving = false;
async function readCart() {
  const response = await fetch(`${cartRoot()}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error('We couldn’t check your cart. Please try again.');
  return response.json();
}
class DewCartDetails extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.rules = { cutoff: Number(this.dataset.cutoffHour || 11), pickupCutoff: Number(this.dataset.pickupCutoffHour || 15), method: this.dataset.savedMethod || 'Delivery', lead: Number(this.dataset.leadDays || 0), closed: (this.dataset.closedDays || '').split(',').filter(Boolean).map(Number) };
    this.method = this.querySelector('[data-edit-method]');
    this.date = this.querySelector('[data-edit-date]');
    this.neededBy = this.querySelector('[data-edit-needed-by]');
    this.message = this.querySelector('[data-edit-message]');
    this.originalMessage = this.message.value;
    this.details = this.querySelector('details');
    this.status = this.querySelector('.dew-cart-details__status');
    this.warning = this.querySelector('.dew-cart-details__warning');
    this.querySelector('[data-save]').addEventListener('click', () => this.save(), options);
    this.querySelector('[data-cancel]').addEventListener('click', () => {
      if (saving) return;
      if (this.date) { this.date.value = this.dataset.savedDate; this.date.setCustomValidity(''); }
      if (this.method) this.method.value = this.dataset.savedMethod || 'Delivery';
      if (this.neededBy) this.neededBy.value = this.dataset.savedNeededBy || '';
      this.message.value = this.originalMessage; this.status.textContent = ''; this.details.open = false;
      this.check();
      this.querySelector('summary').focus();
    }, options);
    this.date?.addEventListener('input', () => this.date.setCustomValidity(''), options);
    this.method?.addEventListener('change', () => { this.date?.setCustomValidity(''); this.check(); }, options);
    window.addEventListener('focus', () => this.check(), options);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) this.check(); }, options);
    this.timer = setInterval(() => this.check(), 30000);
    this.check();
  }
  disconnectedCallback() { this.abort?.abort(); this.abort = null; clearInterval(this.timer); }
  check() {
    this.rules.method = this.dataset.savedMethod || 'Delivery';
    const draftRules = { ...this.rules, method: this.method?.value || this.rules.method };
    if (this.date) {
      this.date.min = earliestDelivery(new Date(), draftRules);
      const help = this.querySelector('[data-method-help]');
      if (help) help.textContent = orderMethodHelp(draftRules);
    }
    const error = this.dataset.needsDate === 'true' ? deliveryError(this.dataset.savedDate || '', new Date(), this.rules) : '';
    this.warning.textContent = error; this.warning.hidden = !error;
    return error;
  }
  async save() {
    if (saving) { this.status.textContent = 'Please wait for the current cart update to finish.'; return; }
    const error = this.date ? deliveryError(this.date.value, new Date(), { ...this.rules, method: this.method?.value || this.rules.method }) : '';
    if (error) { this.date.setCustomValidity(error); this.date.reportValidity(); this.date.focus(); return; }
    saving = true;
    const controls = [...document.querySelectorAll('cart-items-component button, cart-items-component input, cart-items-component textarea, cart-items-component select')].map(control => [control, control.disabled]);
    controls.forEach(([control]) => control.disabled = true);
    this.status.textContent = 'Saving details…';
    try {
      const cart = await readCart();
      const line = cart.items.find(item => item.key === this.dataset.lineKey);
      if (!line) throw new Error('This item changed in your cart. Refresh the page, then try again.');
      const edits = { 'Card message': this.message.value.trim() };
      if (this.date) edits['Delivery date'] = this.date.value;
      if (this.method) edits['Order method'] = this.method.value;
      if (this.neededBy) edits['Needed by'] = this.neededBy.value;
      const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el => el.dataset.sectionId))].slice(0, 5);
      const response = await fetch(`${cartRoot()}cart/change.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...editLineProperties(line, edits), sections, sections_url: location.pathname }),
      });
      const updated = await response.json();
      if (!response.ok || updated.errors) throw new Error(typeof updated.description === 'string' ? updated.description : 'We couldn’t save those details. Please try again.');
      this.status.textContent = 'Details saved.';
      this.dataset.savedDate = edits['Delivery date'] || ''; this.dataset.savedMethod = edits['Order method'] || 'Delivery'; this.dataset.savedNeededBy = edits['Needed by'] || ''; this.originalMessage = edits['Card message']; this.check();
      // Use the same event contract as Horizon quantity updates: refresh page and drawer together.
      this.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [{ id: line.key, quantity: line.quantity }],
        promise: Promise.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updated), detail: {
          sections: updated.sections, items: updated.items, itemCount: updated.item_count, source: 'dew-cart-details', didError: false,
        } }),
      }));
      const component = this.closest('cart-items-component');
      // Morphing replaces a line when its properties/key change. Restore keyboard focus to the saved item's editor.
      const saved = updated.items.find(item => item.variant_id === line.variant_id && item.properties?.['Order method'] === (edits['Order method'] ?? line.properties?.['Order method']) && item.properties?.['Delivery date'] === (edits['Delivery date'] ?? line.properties?.['Delivery date']) && item.properties?.['Card message'] === edits['Card message'] && (item.properties?.['Needed by'] || '') === (edits['Needed by'] ?? item.properties?.['Needed by'] ?? ''));
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const editor = [...(component?.querySelectorAll('dew-cart-details') || [])].find(el => el.dataset.lineKey === saved?.key);
        if (editor) { editor.querySelector('summary')?.focus(); const status = editor.querySelector('.dew-cart-details__status'); status.textContent = 'Details saved.'; }
      }));
    } catch (error) { this.status.textContent = error instanceof Error ? error.message : 'We couldn’t save those details. Please try again.'; }
    finally { saving = false; controls.forEach(([control, disabled]) => { if (control.isConnected) control.disabled = disabled; }); }
  }
}
if (!customElements.get('dew-cart-details')) customElements.define('dew-cart-details', DewCartDetails);

let checking = false;
let confirmed = false;
document.addEventListener('submit', async event => {
  if (!(event.target instanceof HTMLFormElement) || event.target.id !== 'cart-form' || event.submitter?.name !== 'checkout') return;
  if (confirmed) { confirmed = false; return; }
  event.preventDefault(); event.stopImmediatePropagation();
  if (checking) return;
  const form = event.target; const button = event.submitter;
  const status = button.closest('.cart__ctas')?.querySelector('[data-dew-checkout-status]');
  const tell = message => { if (status) { status.textContent = message; status.hidden = false; } };
  if (saving) { tell('Please wait while your cart details are saved.'); return; }
  checking = true; button.disabled = true; tell('Checking your delivery dates…');
  try {
    // Unsaved edits should never appear to be part of the order.
    const dirty = [...document.querySelectorAll('dew-cart-details')].find(el =>
      (el.date && el.date.value !== el.dataset.savedDate) || (el.method && el.method.value !== (el.dataset.savedMethod || 'Delivery')) || (el.neededBy && el.neededBy.value !== (el.dataset.savedNeededBy || '')) || el.message.value !== el.originalMessage);
    if (dirty) { dirty.details.open = true; dirty.querySelector('[data-save]').focus(); throw new Error('Save or cancel your changes before checking out.'); }
    // The buyer's phone must be on the cart before the note is composed (dew-cart-phone.js).
    const phone = button.closest('.cart__ctas')?.querySelector('dew-cart-phone');
    if (phone) await phone.save();
    const cart = await readCart();
    const renderedKeys = new Set([...document.querySelectorAll('tr[data-key]')].map(row => row.dataset.key));
    if (cart.items.some(item => !renderedKeys.has(item.key))) throw new Error('Your cart changed in another tab. Refresh this page to review the latest items before checking out.');
    const invalid = cart.items.find(item => {
      const editor = [...document.querySelectorAll('dew-cart-details')].find(el => el.dataset.lineKey === item.key);
      return (editor?.dataset.needsDate === 'true' || Object.hasOwn(item.properties || {}, 'Delivery date')) && deliveryError(item.properties?.['Delivery date'] || '', new Date(), { ...editor?.rules, method: item.properties?.['Order method'] || 'Delivery' });
    });
    if (invalid) {
      const editor = [...document.querySelectorAll('dew-cart-details')].find(el => el.dataset.lineKey === invalid.key && el.getClientRects().length);
      if (editor) { editor.dataset.savedDate = invalid.properties?.['Delivery date'] || ''; editor.dataset.savedMethod = invalid.properties?.['Order method'] || 'Delivery'; editor.check(); editor.details.open = true; editor.date?.focus(); }
      throw new Error(`Update the delivery or pickup date for ${invalid.product_title} before checking out.`);
    }
    // The order note is what Jen reads first in admin; make sure it reflects the final cart before checkout starts.
    await syncOrderNote();
    // requestSubmit skips the submit event when native validation fails, so never leave the bypass armed.
    confirmed = true; button.disabled = false; form.requestSubmit(button); confirmed = false;
  } catch (error) { tell(error instanceof Error ? error.message : 'We couldn’t check your dates. Please try again.'); }
  finally { checking = false; button.disabled = false; }
}, true);
